import { Icon } from "@blueprintjs/core";
import type { ReactElement } from "react";
import { Fragment, useRef } from "react";
import { Link } from "react-router-dom";
import { getRecordEventId } from "../../utils/recordValues";

export enum TABLE_ACTION {
  PHONEBOOK,
  INFO,
  EVENT,
  DATABASE,
  PLOTS,
  DOWNLOAD,
}

export type TableActionType = TABLE_ACTION | ((data, context) => ReactElement);

const actionLinkClassName = "focusoc-table-action-link";
const actionIconClassName = "focusoc-table-action-icon";

export const ActionsColumnItem = ({ action, data, context }) => {
  const rowId = data.row.original.id;

  switch (action) {
    case TABLE_ACTION.INFO:
      return (
        <Link
          to={`/${context.base}/${rowId}/info`}
          key={`link-info-${rowId}`}
          className={actionLinkClassName}
        >
          <Icon
            icon="eye-open"
            className={actionIconClassName}
            title="See details"
          />
        </Link>
      );
    case TABLE_ACTION.EVENT: {
      const eventId = getRecordEventId(data.row.original);

      if (!eventId) {
        return (
          <Icon
            key={`icon-event-${rowId}`}
            icon="timeline-events"
            className={`${actionIconClassName} focusoc-table-action-icon-disabled disabled-icon-link`}
            title="No event available"
          />
        );
      }

      return (
        <Link
          to={`/event/${eventId}/info`}
          key={`link-event-${rowId}`}
          className={actionLinkClassName}
        >
          <Icon
            icon="timeline-events"
            className={actionIconClassName}
            title="Open event"
          />
        </Link>
      );
    }
    case TABLE_ACTION.DATABASE:
      return (
        <Link
          to={`/${context.base}/${context.source}/${rowId}/info`}
          key={`link-db-${rowId}`}
          className={actionLinkClassName}
        >
          <Icon
            icon="database"
            className={actionIconClassName}
            key={`icon-db-${rowId}`}
            title="Open database record"
          />
        </Link>
      );
    case TABLE_ACTION.PLOTS:
      if (
        data.row.original.plotFiles === "AVAILABLE" ||
        data.row.original.event?.plotFiles === "AVAILABLE"
      ) {
        return (
          <Link
            to={`/${context.base}/${rowId}/info?tab=Plots`}
            key={`link-plot-${rowId}`}
            className={actionLinkClassName}
          >
            <Icon
              icon="timeline-line-chart"
              className={actionIconClassName}
              title="See plots"
              key={`icon-plot-${rowId}`}
            />
          </Link>
        );
      }
      return (
        <Icon
          key={`icon-plot-${rowId}`}
          icon="timeline-line-chart"
          className={`${actionIconClassName} focusoc-table-action-icon-disabled disabled-icon-link`}
          title="No plots available"
        />
      );
    case TABLE_ACTION.DOWNLOAD:
      return (
        <Icon
          key={`icon-download-${rowId}`}
          size={16}
          icon="download"
          title="Download file"
          onClick={() => context.downloadData({ id: rowId })}
          className={actionIconClassName}
        />
      );
    default:
      return null;
  }
};

export const TableActionsColumn = (params) => {
  const { context, data } = params;
  const containerRef = useRef<HTMLDivElement | null>(null);

  if (!data || !context) return null;

  const renderedActions = context.actions.map((action, i) => {
    if (typeof action === "function") {
      return <Fragment key={i}>{action(data, context)}</Fragment>;
    }

    return (
      <ActionsColumnItem
        key={`${action}-${data.id}`}
        action={action}
        data={data}
        context={context}
      />
    );
  });

  return (
    <div
      ref={containerRef}
      className="focusoc-table-action-cell mx-2 flex items-center justify-center gap-1"
    >
      {renderedActions}
    </div>
  );
};
